import brightLightBulb from "@assets/icons/bright-light-bulb.svg";
import closeIcon from "@assets/icons/cross-mark-black.svg";
import { AISummaryAdsLb } from "@elements/Advertisement/variant/AISummaryAds";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { aiSummaryProps } from "./AiSummary";

interface aiSummaryModalProps extends aiSummaryProps {
  /**
   * The page number used for ad targeting.
   */
  pageNumber: number;

  /**
   * The article section name used for ad targeting.
   */
  sectionName?: string;

  /**
   * Google ad channels used for ad targeting.
   */
  gsChannels?: string[];
}

/**
 * Displays an article english summary in a full-screen overlay modal.
 *
 * The modal can be opened using the "Summarise" button and closed using
 * the close button. It also displays an LB ad below the generated summary.
 *
 * @param props - Props used to configure the summary modal.
 * @returns The summary button and overlay modal.
 */
export default function AiSummaryModal({
  englishHeadline,
  englishSummary,
  buttonLabel = "Summarise",
  customGaEvents,
  pageNumber,
  sectionName,
  gsChannels,
}: aiSummaryModalProps) {
  const tmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const pageAdTargetValue = PageAdTargetingTypeEnum.AISUMMARY;
  const [isOpen, setIsOpen] = useState(false);

  const toggleOverlay = () => {
    setIsOpen((prev) => !prev);

    if (isOpen) {
      tmCustomGaEvent({
        event: "customEvent",
        eventCategory: "ai summary",
        eventAction: "click",
        ...customGaEvents,
      });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggleOverlay}
        aria-label={buttonLabel}
        className="flex gap-2 items-center mr-11 py-2"
      >
        <img alt="" src={brightLightBulb} height={22} width={22} />
        <span className="font-primary font-medium text-xs text-black-700">
          {buttonLabel}
        </span>
      </button>

      <div
        className={twMerge(
          "fixed inset-0 z-max bg-white-100 w-full h-full overflow-y-auto transform transition-transform duration-300 ease-out",
          isOpen
            ? "translate-y-0 opacity-100"
            : "translate-y-full pointer-events-none opacity-0",
        )}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        <button
          type="button"
          onClick={toggleOverlay}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 lg:items-center justify-center rounded-full text-xl text-gray-100"
        >
          <img alt="Close" src={closeIcon} height={32} width={32} />
        </button>

        <div className="min-h-full w-full px-200 pt-12 lg:pb-8 lg:flex lg:items-center lg:justify-center lg:px-0 lg:py-24">
          <div className="relative z-10 w-full max-w-2md grid gap-10">
            <div className="font-primary grid gap-6">
              <span className="text-red-100 font-normal text-xs leading-200">
                Generated by AI
              </span>

              <h2 className="text-2.5md leading-8 font-bold text-gray-100">
                {englishHeadline}
              </h2>

              <p className="text-gray-100 text-md font-normal leading-7">
                {englishSummary}
              </p>
            </div>

            <div className="py-xxs">
              <AISummaryAdsLb
                uniqueSectionName={sectionName}
                pageAdTargetType={pageAdTargetValue}
                index={pageNumber}
                gsChannels={gsChannels}
                rootClassName="my-0 lg:h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
